import { fetchClient } from "@/utils/fetch.ts";
import { extractPublicKey } from "./public-key";

interface Passkey {
	name: string;
	challenge: string;
	publicKey: string;
}

export async function getChallenge(): Promise<Uint8Array<ArrayBuffer>> {
	const { data, error } = await fetchClient.GET("/api/auth/challenge");
	if (error) {
		throw new Error(error.errorMessage);
	}
	return Uint8Array.fromBase64(data.challenge, {
		alphabet: "base64url",
	});
}

/**
 * Get public key and challenge
 */
export async function registerPasskey(
	email: string,
	name?: string,
): Promise<Passkey> {
	const challenge = await getChallenge();
	const credential = (await navigator.credentials.create({
		publicKey: {
			challenge,
			rp: {
				id: window.location.hostname,
				name: "oneAI",
			},
			user: {
				name: email,
				displayName: name ?? `Passkey ${new Date().toLocaleDateString()}`,
				id: challenge,
			},
			authenticatorSelection: {
				authenticatorAttachment: "platform",
				residentKey: "required",
				requireResidentKey: true,
				userVerification: "required",
			},
			pubKeyCredParams: [
				{ alg: -8, type: "public-key" }, // EdDSA
				{ alg: -7, type: "public-key" }, // ES256
				{ alg: -257, type: "public-key" }, // RS256
			],
		},
	})) as PublicKeyCredential;
	const publicKey = await extractPublicKey(credential);
	return {
		name: name ?? `Passkey ${new Date().toLocaleDateString()}`,
		challenge: new Uint8Array(challenge).toBase64({
			alphabet: "base64url",
			omitPadding: true,
		}),
		publicKey: new Uint8Array(publicKey).toBase64(),
	};
}
